import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import { ALL_CLIENT_ROLES } from '@/types/auth';
import { fetchMyProductCatalog, type ProductCatalogItem } from '@/features/client-requests/api';

export type { ProductCatalogItem };

/**
 * Fetches the full product catalog with ACTIVE/LOCKED status for the authenticated client.
 * Shared across the sidebar, product-gated routes, and the locked product experience.
 * Disabled for non-client users (admin staff bypass all product gates).
 */
export function useClientProductAccess() {
  const { hasAnyRole } = useAuth();
  const isClientUser = hasAnyRole(ALL_CLIENT_ROLES);

  const query = useQuery({
    queryKey: ['me', 'product-catalog'],
    queryFn: fetchMyProductCatalog,
    staleTime: 60_000,
    enabled: isClientUser,
  });

  function getStatus(code: string): 'ACTIVE' | 'LOCKED' | undefined {
    return query.data?.find((p) => p.productCode === code)?.status;
  }

  function hasProduct(code: string): boolean {
    return getStatus(code) === 'ACTIVE';
  }

  function lockedProducts(): ProductCatalogItem[] {
    return query.data?.filter((p) => p.status === 'LOCKED') ?? [];
  }

  return {
    products: query.data ?? [],
    hasProduct,
    getStatus,
    lockedProducts,
    isClientUser,
    isLoading: query.isLoading,
  };
}
